'use client'

import { useGraphic } from '@/lib/features/graphic/GraphicContext'
import { useGraphicHistory } from '@/lib/features/graphic/GraphicHistoryContext'
import { ChevronDown, Download, Edit2, FileImage, FileText } from 'lucide-react'
import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { graphicsCreator } from 'sprites-types/workflow/tools/graphicsCreator'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import { FileResult } from '../shared/FileResult'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

// Helper function to extract graphic ID from URL
const extractGraphicId = (sourceUrl: string): string | null => {
  const parts = sourceUrl.split('/')
  const indexOfGraphics = parts.indexOf('graphics')
  if (indexOfGraphics !== -1) {
    return parts[indexOfGraphics + 1]!.replace('.html', '')
  }
  return null
}

// Determine file type from fileType field
const getFileInfo = (fileType: 'image' | 'pdf') => {
  if (fileType === 'pdf') {
    return {
      type: 'pdf',
      icon: FileText,
      label: 'PDF Document',
      canPreview: true,
    }
  } else {
    return {
      type: 'image',
      icon: FileImage,
      label: 'Image',
      canPreview: true,
    }
  }
}

// Single graphic item component - with its own ContextMenuWrapper
interface SingleGraphicItemProps {
  graphicResult: {
    type: 'file'
    fileType: 'image' | 'pdf'
    filename: string
    source: { kind: 'url' | 'raw'; value: string }
  }
  initialGraphicId?: string | null
  className?: string
}

function SingleGraphicItem({ graphicResult, initialGraphicId, className }: SingleGraphicItemProps) {
  const { fetchGraphic } = useGraphic()
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)

  const { getGraphicData, setCurrentGraphicId, addGraphic, setCurrentEditingInitialId } =
    useGraphicHistory()

  // Get graphic history data if available
  const graphicData = initialGraphicId ? getGraphicData(initialGraphicId) : null
  const { history = [], currentGraphicId } = graphicData || {}

  const graphicId = currentGraphicId || initialGraphicId
  const graphicUrl = graphicResult.source.value
  const displayUrl = history.find((item) => item.graphicId === graphicId)?.previewUrl || graphicUrl

  const fileInfo = getFileInfo(graphicResult.fileType)

  const handleEditGraphic = useCallback(async () => {
    if (!initialGraphicId) {
      toast.error('No graphic ID found for editing')
      return
    }

    if (!graphicId) {
      toast.error('No graphic ID found')
      return
    }

    console.log('Fetching graphicId', graphicId)

    if (history.length === 0) {
      addGraphic(initialGraphicId, initialGraphicId, graphicUrl)
    }

    const result_fetch = await fetchGraphic(graphicId)
    if (!result_fetch.success) {
      toast.error('Failed to fetch, please try again')
    } else {
      setCurrentEditingInitialId(initialGraphicId)
    }
  }, [
    graphicId,
    fetchGraphic,
    history,
    initialGraphicId,
    addGraphic,
    graphicUrl,
    setCurrentEditingInitialId,
  ])

  const handleHistorySelect = useCallback(
    (selectedGraphicId: string) => {
      if (initialGraphicId) {
        setCurrentGraphicId(initialGraphicId, selectedGraphicId)
        setIsHistoryOpen(false)
      }
    },
    [initialGraphicId, setCurrentGraphicId]
  )

  const getCurrentHistoryLabel = useCallback(() => {
    if (!currentGraphicId || currentGraphicId === initialGraphicId) {
      return 'Original'
    }

    const index = history.findIndex((item) => item.graphicId === currentGraphicId)
    if (index === -1) return 'Original'

    return `Edit ${index}`
  }, [currentGraphicId, initialGraphicId, history])

  // Generate context actions for this individual item
  const contextActions: ContextAction[] = []

  // Edit action (only if we have a graphic ID)
  if (initialGraphicId) {
    contextActions.push({
      id: 'edit',
      label: 'Edit',
      icon: <Edit2 className="h-4 w-4" />,
      onClick: handleEditGraphic,
    })
  }

  // For simplicity, let's put the history dropdown in the context actions
  if (history.length > 1) {
    contextActions.push({
      id: 'history',
      label: 'Version History',
      icon: <ChevronDown className="h-4 w-4" />,
      onClick: () => setIsHistoryOpen(!isHistoryOpen),
    })
  }

  return (
    <div className={className}>
      <FileResult
        file={{
          name: `Generated ${fileInfo.label}`,
          url: displayUrl,
          downloadUrl: displayUrl,
          mimeType: fileInfo.type === 'pdf' ? 'application/pdf' : 'image/png',
          id: initialGraphicId || 'graphic',
        }}
        customIcon={<fileInfo.icon className="h-5 w-5 text-gray-600" />}
        customLabel={`Generated ${fileInfo.label}`}
        customSubtitle={history.length > 0 ? getCurrentHistoryLabel() : 'Ready to use'}
        contextActions={contextActions}
        showPreview={true}
      />

      {/* History dropdown overlay */}
      {isHistoryOpen && history.length > 1 && (
        <div className="relative">
          <div className="absolute right-4 top-2 z-50 min-w-[120px] rounded-md border border-gray-600 bg-gray-800 shadow-lg">
            {history.map((item, index) => (
              <button
                key={item.graphicId}
                className="w-full px-3 py-2 text-left text-xs text-white first:rounded-t-md last:rounded-b-md hover:bg-gray-700"
                onClick={() => handleHistorySelect(item.graphicId)}
              >
                {index === 0 ? 'Original' : `Edit ${index}`}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function GraphicsCreatorDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const [isExpanded, setIsExpanded] = useState(true)

  const schema = getToolResultSchema(graphicsCreator, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  // Handle bulk download for arrays
  const handleBulkDownload = async () => {
    if (!Array.isArray(result)) return

    for (const [index, item] of result.entries()) {
      try {
        const response = await fetch(item.source.value)
        if (!response.ok) throw new Error('Network response was not ok')

        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)
        const fileInfo = getFileInfo(item.fileType)

        const downloadLink = document.createElement('a')
        downloadLink.href = blobUrl
        downloadLink.download =
          item.filename || `graphic-${index + 1}.${fileInfo.type === 'pdf' ? 'pdf' : 'png'}`
        downloadLink.click()

        window.URL.revokeObjectURL(blobUrl)

        if (index < result.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100))
        }
      } catch (error) {
        console.error(`Download failed for item ${index + 1}:`, error)
        toast.error(`Download failed for item ${index + 1}`)
      }
    }

    toast.success(`Downloaded ${result.length} graphics`)
  }

  if (Array.isArray(result)) {
    // Header actions for bulk operations
    const headerActions: ContextAction[] = [
      {
        id: 'download-all',
        label: 'Download All',
        icon: <Download className="h-4 w-4" />,
        onClick: handleBulkDownload,
      },
    ]

    return (
      <div className="space-y-3">
        {/* Header with its own context menu and collapse/expand */}
        <ContextMenuWrapper actions={headerActions}>
          <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
              aria-expanded={isExpanded}
            >
              <div className="flex-shrink-0">
                <FileImage className="h-5 w-5 text-gray-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-gray-900">
                  Generated Graphics
                </div>
                <div className="text-xs font-medium tracking-wide text-gray-500">
                  {result.length} items
                </div>
              </div>
              <div className="flex-shrink-0">
                <ChevronDown
                  className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </div>
            </button>
          </div>
        </ContextMenuWrapper>

        {/* Individual items - show/hide based on expanded state */}
        {isExpanded && (
          <div className="space-y-2">
            {result.map((item, index) => (
              <SingleGraphicItem
                key={`${item.source.value}-${index}`}
                graphicResult={item}
                initialGraphicId={extractGraphicId(item.source.value)}
                className="mx-8"
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  // Single item - render directly with its own context menu
  return (
    <SingleGraphicItem
      graphicResult={result}
      initialGraphicId={extractGraphicId(result.source.value)}
    />
  )
}

async function downloadGraphic(nodeExecution: NodeExecution) {
  const schema = getToolResultSchema(graphicsCreator, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    console.error('Schema validation failed for download:', parseResult.error)
    return Promise.resolve()
  }

  const result = parseResult.data
  const items = []

  if (!Array.isArray(result)) {
    items.push(result)
  } else {
    items.push(...result)
  }

  if (items.length === 0) {
    toast.error('No items found')
    return
  }

  for (const [index, item] of items.entries()) {
    const fileInfo = getFileInfo(item.fileType)
    try {
      const response = await fetch(item.source.value)
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)

      const downloadLink = document.createElement('a')
      downloadLink.href = blobUrl
      downloadLink.download =
        item.filename ||
        `${nodeExecution.nodeId}-${index + 1}.${fileInfo.type === 'pdf' ? 'pdf' : 'png'}`
      downloadLink.click()

      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Download failed:', error)
      toast.error('Download failed')
    }
  }
}

// Convert graphics creator results to markdown
function graphicsCreatorToMarkdown(nodeExecution: NodeExecution): string {
  try {
    const schema = getToolResultSchema(graphicsCreator, nodeExecution.inputs)
    const parseResult = schema.safeParse(nodeExecution.outputs)

    if (!parseResult.success) {
      return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
    }

    const result = parseResult.data
    const items = []
    if (!Array.isArray(result)) {
      items.push(result)
    } else {
      items.push(...result)
    }

    const fileMarkdown = items.map((item, index) => {
      const url = item.source.value
      if (item.fileType === 'image') {
        return `![Generated Image ${index + 1}](${url})\n`
      } else {
        return `[Generated PDF ${index + 1}](${url})\n`
      }
    })

    if (fileMarkdown.length === 1) {
      return fileMarkdown[0]
    } else {
      return `## Generated ${items.length} Graphics:
         - ${fileMarkdown.join('\n- ')}`
    }
  } catch (error) {
    return `## Graphics Creator\n\nError parsing results: ${error}\n\n---\n`
  }
}

// Export the component with markdown support
export const GraphicsCreatorDisplay = createWorkflowToolComponent(
  GraphicsCreatorDisplayComponent,
  downloadGraphic,
  graphicsCreatorToMarkdown
)
